import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { type ColumnDef } from '@tanstack/react-table'
import DataTable from '../../components/DataTable'
import Button from '../components/Button'
import Icon from '../components/Icon'
import LogAccommodation from '../log/LogAccommodation'
import { useClassStore } from '../../stores/classesStore'
import { useSchoolStore } from '../../stores/schoolsStore'
import { Class as ClassType, Student, School } from '../../types'

const Class: React.FC = () => {
  const { classId } = useParams<{ classId: string }>()
  const navigate = useNavigate()

  const [isLogModalOpen, setIsLogModalOpen] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null)

  const {
    teacherClasses,
    teacherClassesLoading,
    teacherClassesError,
    fetchTeacherClasses,
  } = useClassStore()

  const { schools, loading: schoolsLoading, fetchSchools } = useSchoolStore()

  // Fetch teacher classes and schools on component mount
  useEffect(() => {
    fetchTeacherClasses()
    fetchSchools()
  }, [fetchTeacherClasses, fetchSchools])

  // Find the current class from teacherClasses
  const currentClass = useMemo(() => {
    return teacherClasses.find((cls: ClassType) => cls._id === classId)
  }, [teacherClasses, classId])

  // Get students from the current class
  const students = useMemo(() => {
    if (!currentClass || !currentClass.students) return []

    // If students are populated (objects), return them directly
    if (
      currentClass.students.length > 0 &&
      typeof currentClass.students[0] === 'object'
    ) {
      return currentClass.students as unknown as Student[]
    }

    // If students are just IDs, we'd need to fetch them separately
    // For now, return empty array as we need populated student data
    return []
  }, [currentClass])

  const getSchoolName = (schoolId: string) => {
    const school = schools.find((school: School) => school._id === schoolId)
    return school?.displayName || 'Unknown School'
  }

  const formatAccommodations = (accommodations: string[]) => {
    if (!accommodations || accommodations.length === 0) return 'None'
    return accommodations.length === 1
      ? '1 accommodation'
      : `${accommodations.length} accommodations`
  }

  const handleLogAccommodation = (student: Student) => {
    setSelectedStudent(student)
    setIsLogModalOpen(true)
  }

  const handleCloseLogModal = () => {
    setIsLogModalOpen(false)
    setSelectedStudent(null)
  }

  // Define columns for students DataTable
  const columns = useMemo<ColumnDef<Student>[]>(
    () => [
      {
        accessorKey: 'firstName',
        header: 'Name',
        cell: (info) => {
          const student = info.row.original
          return (
            <div className="text-sm font-medium text-gray-900">
              {student.firstName} {student.lastName}
            </div>
          )
        },
      },
      {
        accessorKey: 'grade',
        header: 'Grade',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: 'race',
        header: 'Race',
        cell: (info) => (
          <div className="text-sm text-gray-500">
            {(info.getValue() as string) || 'Not specified'}
          </div>
        ),
      },
      {
        accessorKey: 'gender',
        header: 'Gender',
        cell: (info) => (
          <div className="text-sm text-gray-500">
            {(info.getValue() as string) || 'Not specified'}
          </div>
        ),
      },
      {
        accessorKey: 'caseManager',
        header: 'Case Manager',
        cell: (info) => (
          <div className="text-sm text-gray-500">
            {(info.getValue() as string) || 'Not assigned'}
          </div>
        ),
      },
      {
        accessorKey: 'accommodations',
        header: 'Accommodations',
        cell: (info) => {
          const accommodations = info.getValue() as string[]
          return (
            <div
              className="text-sm text-gray-500"
              title={accommodations?.join(', ') || 'None'}
            >
              {formatAccommodations(accommodations)}
            </div>
          )
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex space-x-2">
            <Button
              onClick={() => handleLogAccommodation(row.original)}
              variant="link"
              className="mr-3"
              title="Log accommodation for this student"
            >
              <span>Log Accommodation</span>
            </Button>
          </div>
        ),
      },
    ],
    [],
  )

  if (teacherClassesLoading || schoolsLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // If class not found or user doesn't have access
  if (!currentClass) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Icon type="building" className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Class not found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            The class you're looking for doesn't exist or you don't have access
            to it.
          </p>
          <div className="mt-6">
            <Button onClick={() => navigate('/user/classes')} variant="primary">
              Back to My Classes
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const schoolName =
    typeof currentClass.school === 'object' && currentClass.school.displayName
      ? currentClass.school.displayName
      : getSchoolName(
          typeof currentClass.school === 'object'
            ? currentClass.school._id
            : currentClass.school,
        )

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => navigate('/user/classes')}
              variant="ghost"
              className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-0"
            >
              <Icon type="chevron-left" className="w-5 h-5" />
            </Button>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
              {currentClass.name}
            </h1>
          </div>
          <p className="text-gray-600 text-sm sm:text-base mt-1">
            {schoolName} • {students.length} student
            {students.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Error Display */}
      {teacherClassesError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {teacherClassesError}
        </div>
      )}

      {/* Students Table */}
      <DataTable
        data={students}
        columns={columns}
        searchPlaceholder="Search students..."
        emptyStateTitle="No students enrolled"
        emptyStateDescription="This class doesn't have any students enrolled yet."
        emptyStateIcon={
          <Icon type="users" className="mx-auto h-12 w-12 text-gray-400" />
        }
        loading={teacherClassesLoading || schoolsLoading}
        showCreateButton={false}
        onRowClick={(studentItem) => {
          setSelectedStudent(studentItem)
          setIsLogModalOpen(true)
        }}
      />

      {/* Log Accommodation Modal */}
      {selectedStudent && classId && (
        <LogAccommodation
          isOpen={isLogModalOpen}
          onClose={handleCloseLogModal}
          student={selectedStudent}
          classId={classId}
        />
      )}
    </div>
  )
}

export default Class
